/**
 * Client-side rate limiting utility for form submissions
 * Prevents spam and abuse while providing user feedback
 */

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number; // Time window in milliseconds
  blockDurationMs: number; // How long to block after limit exceeded
}

interface RateLimitState {
  attempts: number[];
  blockedUntil: number | null;
  lastAttempt: number;
}

class RateLimiter {
  private state: RateLimitState = {
    attempts: [],
    blockedUntil: null,
    lastAttempt: 0
  };

  constructor(private config: RateLimitConfig) {}

  /**
   * Check if an action is allowed and record the attempt
   */
  checkAndRecord(): { allowed: boolean; remainingTime?: number; remainingAttempts?: number } {
    const now = Date.now();

    // Check if currently blocked
    if (this.state.blockedUntil && now < this.state.blockedUntil) {
      return {
        allowed: false,
        remainingTime: this.state.blockedUntil - now
      };
    }

    // Clean old attempts outside the window
    this.state.attempts = this.state.attempts.filter(
      timestamp => now - timestamp < this.config.windowMs
    );

    // Check if limit exceeded
    if (this.state.attempts.length >= this.config.maxAttempts) {
      this.state.blockedUntil = now + this.config.blockDurationMs;
      return {
        allowed: false,
        remainingTime: this.config.blockDurationMs
      };
    }

    // Record the attempt
    this.state.attempts.push(now);
    this.state.lastAttempt = now;

    return {
      allowed: true,
      remainingAttempts: this.config.maxAttempts - this.state.attempts.length
    };
  }

  /**
   * Get current status
   */
  getStatus() {
    const now = Date.now();
    const isBlocked = this.state.blockedUntil && now < this.state.blockedUntil;
    const remainingTime = isBlocked ? this.state.blockedUntil! - now : 0;
    const attemptsInWindow = this.state.attempts.filter(
      timestamp => now - timestamp < this.config.windowMs
    ).length;

    return {
      isBlocked,
      remainingTime,
      attemptsInWindow,
      remainingAttempts: Math.max(0, this.config.maxAttempts - attemptsInWindow)
    };
  }

  /**
   * Reset the rate limiter (for testing or manual reset)
   */
  reset(): void {
    this.state = {
      attempts: [],
      blockedUntil: null,
      lastAttempt: 0
    };
  }
}

// Global rate limiter instances
const registrationLimiter = new RateLimiter({
  maxAttempts: 5, // 5 registration attempts
  windowMs: 15 * 60 * 1000, // 15 minutes
  blockDurationMs: 30 * 60 * 1000 // 30 minutes block
});

const loginLimiter = new RateLimiter({
  maxAttempts: 10, // 10 login attempts
  windowMs: 15 * 60 * 1000, // 15 minutes
  blockDurationMs: 15 * 60 * 1000 // 15 minutes block
});

const generalFormLimiter = new RateLimiter({
  maxAttempts: 20, // 20 general form submissions
  windowMs: 60 * 60 * 1000, // 1 hour
  blockDurationMs: 60 * 60 * 1000 // 1 hour block
});

/**
 * Rate limiting hooks for different form types
 */
export const useRegistrationRateLimit = () => {
  return {
    checkLimit: () => registrationLimiter.checkAndRecord(),
    getStatus: () => registrationLimiter.getStatus(),
    reset: () => registrationLimiter.reset()
  };
};

export const useLoginRateLimit = () => {
  return {
    checkLimit: () => loginLimiter.checkAndRecord(),
    getStatus: () => loginLimiter.getStatus(),
    reset: () => loginLimiter.reset()
  };
};

export const useGeneralFormRateLimit = () => {
  return {
    checkLimit: () => generalFormLimiter.checkAndRecord(),
    getStatus: () => generalFormLimiter.getStatus(),
    reset: () => generalFormLimiter.reset()
  };
};

/**
 * Utility function to format remaining time
 */
export const formatRemainingTime = (ms: number): string => {
  const minutes = Math.ceil(ms / (1000 * 60));
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.ceil(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
};